import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import { FormattedMessage, defineMessages } from 'react-intl';
import type { NoMessageValues, MessageValue } from 'react-intl';
import type { SearchResultCategoryFilterFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchResultCategoryFilterFragmentFragmentDoc';
import { useOriginalSearchQuery } from '@packages/search/src/hooks/useOriginalSearchQuery';
import { CategoryTreeMobileFirst } from '../CategoryTreeMobileFirst/CategoryTreeMobileFirst';
import { useSearchResultDataForCategoryFilter } from './useSearchResultDataForCategoryFilter';

/* GraphQL */ `
  fragment SearchResultCategoryFilterFragment on SuccessfulSearchResult {
    ...UseSearchResultDataForCategoryFilterFragment
    recoveryStrategy {
      ... on DeepQuery {
        alternateQuery
      }
    }
  }
`;
const messages = defineMessages<{
  readonly categoryFilterDeepQueryDescription: NoMessageValues;
  readonly categoryFilterDescription: { readonly filterSearchQuery: MessageValue };
}>({
  categoryFilterDeepQueryDescription: {
    id: 'search.filters.category.deepQueryDescription',
    defaultMessage: 'Stöbere in unseren Sortimenten nach Vorschlägen.',
  },
  categoryFilterDescription: {
    id: 'search.filters.category.description',
    defaultMessage: 'Wir haben "{filterSearchQuery}" in folgenden Sortimenten gefunden:',
  },
});
export type SearchResultCategoryFilterProps = {
  maskedData: FragmentType<typeof SearchResultCategoryFilterFragmentFragmentDoc>;
  /** If `true`, shows a dedicated "clear all" interaction/button in addition to the root level category */
  showClearAll?: boolean;
  /** Callback fired when a category is clicked or all categories are cleared */
  onNavigate?: () => void;
};

const CategoryDescriptionHeader = ({ isDeepQuery }: { isDeepQuery: boolean }) => {
  const originalQuery = useOriginalSearchQuery();

  return (
    <p className="text-size-body-base p-1">
      {isDeepQuery ? (
        <FormattedMessage
          key="categoryFilterDeepQueryDescription"
          {...messages.categoryFilterDeepQueryDescription}
        />
      ) : (
        <FormattedMessage
          key="categoryFilterDescription"
          {...messages.categoryFilterDescription}
          values={{ filterSearchQuery: originalQuery }}
        />
      )}
    </p>
  );
};
/**
 * Formats SERP category navigation items to be used in CategoryTreeMobileFirst component
 * */
export const SearchResultCategoryFilter = ({
  maskedData,
  showClearAll,
  onNavigate,
}: SearchResultCategoryFilterProps) => {
  const data = unmask<typeof SearchResultCategoryFilterFragmentFragmentDoc>(maskedData);

  const items = useSearchResultDataForCategoryFilter(data);
  const isDeepQuery = data.recoveryStrategy !== null && 'alternateQuery' in data.recoveryStrategy;

  return (
    <CategoryTreeMobileFirst
      categoryDescription={<CategoryDescriptionHeader isDeepQuery={isDeepQuery} />}
      categories={items}
      showClearAll={showClearAll}
      onNavigate={onNavigate}
    />
  );
};
